import React, { useEffect } from 'react';

import { loadAllFonts } from '@aline/fonts/client';
import { routes } from '@aline/routes';
import { makeVar } from '@apollo/client';
import { ApolloProvider, useReactiveVar } from '@apollo/client/react';
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { setUseWhatChange } from '@simbathesailor/use-what-changed';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  Route,
  BrowserRouter as Router,
  Routes as RouterRoutes,
  useNavigate,
} from 'react-router-dom';
import { useAsync, useInterval } from 'react-use';
import { getAuthRedirectURI } from 'src/auth';
import { isDev } from 'src/config';
import { apolloClient } from 'src/gql/apollo-client';
import { FileStackProvider } from 'src/helpers/integrations';
import { Routes } from 'src/Routes';
import { themeV5 } from 'src/theme';

import { AppErrorBoundary } from './AppErrorBoundary';
import { AppLoading } from './AppLoading';
import { DeactivatedUserPage } from './components';
import { ExternalToolkitConnectedPage } from './components/admin/integrations/ExternalToolkitConnectedPage';
import { IntercomProvider } from './components/intercom';
import { PreOnboardingContainer } from './components/onboarding/OnboardingContainer';
import { useRedirectPath, useSetRedirectPath } from './hooks';
import { Login, Signup } from './Login';
import { PreLogin } from './PreLogin';
import { useReactSpringReducedMotion } from './react-spring';
import { LaunchPage } from './workflow';

/**
 * Allow debugging of hooks and dependency arrays in dev.  See https://github.com/simbathesailor/use-what-changed.
 */
setUseWhatChange(isDev);

export const Root = function Root(): JSX.Element {
  return (
    <IntercomProvider>
      <ApolloProvider client={apolloClient}>
        <StyledEngineProvider injectFirst>
          <MuiThemeProvider theme={themeV5}>
            <AppErrorBoundary>
              <CssBaseline />
              <DndProvider backend={HTML5Backend}>
                <Router>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <App />
                  </LocalizationProvider>
                </Router>
              </DndProvider>
            </AppErrorBoundary>
          </MuiThemeProvider>
        </StyledEngineProvider>
      </ApolloProvider>
    </IntercomProvider>
  );
};

function App(): JSX.Element | null {
  useLoadFonts();
  useSetRedirectPath();
  useReactSpringReducedMotion();

  return (
    <RouterRoutes>
      <Route element={<Login />} path={routes.Login} />
      <Route element={<Signup />} path={routes.Signup} />
      <Route element={<PreOnboardingContainer />} path={routes.Setup} />
      <Route element={<DeactivatedUserPage />} path={routes.Deactivated} />
      <Route element={<ExternalToolkitConnectedPage />} path={routes.ExternalToolkitConnected} />
      <Route element={<PreLogin accessRequestedOverride={null} />} path={routes.PreLogin} />
      <Route
        element={
          <PreLogin
            accessRequestedOverride={{
              msg: 'A magic link has been emailed to you to access the requested resource directly.  Use the link in your email to access the requested resource.',
              title: 'Check your email',
            }}
          />
        }
        path={routes.MagicLinkSent}
      />
      <Route
        element={
          <PreLogin
            accessRequestedOverride={{
              msg: 'Your magic link has expired and a new link has been emailed to you.  Use the link in your email to access the requested resource.',
              title: 'Link expired, check email',
            }}
          />
        }
        path={routes.MagicLinkResent}
      />
      <Route element={<LaunchPage />} path={routes.WorkflowLaunchConfig} />
      <Route element={<AuthenticatedRoutes />} path="*" />
    </RouterRoutes>
  );
}

function AuthenticatedRoutes(): JSX.Element | null {
  const { sessionValidated } = useSetValidatedSession();

  // This prevents errors when hitting the API before session initialization.
  if (!sessionValidated) return <AppLoading waitingText="Checking authentication..." />;

  // Ensure FileStackProvider is initialized *after* session validation b/c it does a GQL call to get upload security.
  return (
    <FileStackProvider>
      <Routes />
    </FileStackProvider>
  );
}

const FIVE_MINUTES_MS = 5 * 60 * 1000;
export const sessionValidatedVar = makeVar<boolean>(false);

function useSetValidatedSession(): { sessionValidated: boolean } {
  const navigate = useNavigate();
  const { redirectPath } = useRedirectPath();
  const sessionValidated = useReactiveVar(sessionValidatedVar);

  async function checkAuth(path: string) {
    const { redirectUri } = await getAuthRedirectURI(path, url =>
      navigate(`${url.pathname}${url.search}${url.hash}`, { replace: true })
    );
    if (!redirectUri) return { redirected: false };
    if (!redirectUri.pageLoad) navigate(redirectUri.route);
    else window.location.href = redirectUri.route;

    return { redirected: true };
  }

  // Validate session immediately on mount.
  useAsync(async () => {
    const { redirected } = await checkAuth(redirectPath ?? routes.Home);
    if (!redirected) sessionValidatedVar(true);
  }, []);

  // Periodically refresh session to keep cookies fresh for WebSocket subscriptions.
  useInterval(async () => {
    await checkAuth(redirectPath ?? routes.Home);
  }, FIVE_MINUTES_MS);

  return { sessionValidated };
}

export const fontsLoadedVar = makeVar<boolean>(false);
export function useFontsLoaded(): boolean {
  return useReactiveVar(fontsLoadedVar);
}

/**
 * Load necessary fonts at the root of the app.  This is needed so that users can export PDFs in the app with the correct font measurements,
 * and so the Editor has the necessary fonts when it loads a document.
 */
export function useLoadFonts(): void {
  useEffect(() => {
    async function setFontsLoaded(): Promise<void> {
      await loadAllFonts();
      fontsLoadedVar(true);
    }

    setFontsLoaded();
  }, []);
}
